import { FC } from 'react';
import { Col, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { ApplicantDemand } from 'Types';
import { Card } from 'Elements';
import { formatLocaleDateTime } from 'Utils';
import { SkeletonRequestInformation } from './skeleton/SkeletonRequestInformation';

interface Props {
	request?: ApplicantDemand;
	isLoading: boolean;
}

export const RequestInformation: FC<Props> = ({ request, isLoading }) => {
	const { t } = useTranslation();
	const applicant = request?.applicantInfo;
	const applicantName = applicant
		? `${applicant?.firstName} ${applicant?.lastName}`
		: '---';

	const gender = {
		0: 'forms.gender.unknown',
		1: 'forms.gender.male',
		2: 'forms.gender.female'
	};
	return (
		<Card className="h-100">
			<h6 className="text-gray-3 mb-3 fs-6 fw-400">
				{t('title.applicant-information')}
			</h6>
			{isLoading ? (
				<SkeletonRequestInformation />
			) : (
				<Row>
					<Col xl={6}>
						<div className="d-flex align-items-center lh-base mb-3">
							<span className="fw-500 align-self-start">
								{t('forms.name')}:
							</span>
							<span className="ms-2">{applicantName}</span>
						</div>
						<div className="d-flex align-items-center lh-base mb-3">
							<span className="fw-500 align-self-start">
								{t('forms.date-of-birth')}:
							</span>
							<span className="ms-2">
								{applicant?.birthDate
									? formatLocaleDateTime(
											applicant?.birthDate,
											false
									  )
									: '----'}
							</span>
						</div>
						<div className="d-flex align-items-center lh-base mb-3">
							<span className="fw-500 align-self-start">
								{t('forms.gender.key')}:
							</span>
							<span className="ms-2">
								{t(gender[applicant?.gender ?? '']) ?? '---'}
							</span>
						</div>
						<div className="d-flex align-items-center lh-base mb-3">
							<span className="fw-500 align-self-start">
								{t('forms.street')}:
							</span>
							<span className="ms-2">
								{applicant?.address?.street || '---'}
							</span>
						</div>
						<div className="d-flex align-items-center lh-base mb-3">
							<span className="fw-500 align-self-start">
								{t('forms.house-number')}:
							</span>
							<span className="ms-2">
								{applicant?.address?.houseNumber || '---'}
							</span>
						</div>
						<div className="d-flex align-items-center lh-base mb-3">
							<span className="fw-500 align-self-start">
								{t('forms.postal-code')}:
							</span>
							<span className="ms-2">
								{applicant?.address?.postalCode || '---'}
							</span>
						</div>
						<div className="d-flex align-items-center lh-base mb-3">
							<span className="fw-500 align-self-start">
								{t('forms.city')}:
							</span>
							<span className="ms-2">
								{applicant?.address?.city || '---'}
							</span>
						</div>
						<div className="d-flex align-items-center lh-base mb-3">
							<span className="fw-500 align-self-start">
								{t('forms.state')}:
							</span>
							<span className="ms-2">
								{applicant?.address?.state || '---'}
							</span>
						</div>
					</Col>
					<Col xl={6}>
						<div className="d-flex align-items-center lh-base mb-3">
							<span className="fw-500 align-self-start">
								{t('forms.email')}:
							</span>
							<span className="ms-2">
								{applicant?.email || '---'}
							</span>
						</div>
						<div className="d-flex align-items-center lh-base mb-3">
							<span className="fw-500 align-self-start">
								{t('forms.tel')}:
							</span>
							<span className="ms-2">
								{applicant?.mobileNumber || '---'}
							</span>
						</div>
						<div className=" lh-base mb-3">
							<span className="fw-500 align-self-start">
								{t('forms.applicant-comment')}:
							</span>
							<div>{request?.description || '---'}</div>
						</div>
					</Col>
				</Row>
			)}
		</Card>
	);
};
